import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiPost, apiGet, apiPatch, apiDelete, authTokens } from '@/lib/api'
import {
  User,
  LoginForm,
  RegisterForm,
  ForgotPasswordForm,
  ResetPasswordForm,
  MagicLinkRequestForm,
  MagicLinkVerifyForm,
  ChangePasswordForm,
  EmailChangeRequestForm,
  MFASetupResponse,
  MFAVerifySetupForm,
  MFAVerifyForm,
  MFARegenerateBackupCodesResponse,
  MFADisableForm,
  MFARegenerateBackupCodesForm,
  SessionListResponse,
  SessionRevokeResponse,
  SessionRevokeAllResponse,
  DeleteAccountForm,
  DeleteAccountResponse,
  SecurityActivityResponse,
  AvatarUploadResponse,
  AvatarDeleteResponse,
} from '@/types'
import { useAuth } from '@/providers/auth-provider'

// Auth API endpoints
const AUTH_ENDPOINTS = {
  login: '/auth/login',
  register: '/auth/register',
  forgotPassword: '/auth/forgot-password',
  resetPassword: '/auth/reset-password',
  changePassword: '/auth/change-password',
  requestEmailChange: '/auth/request-email-change',
  refresh: '/auth/refresh',
  logout: '/auth/logout',
  profile: '/users/profile',
  avatar: '/users/avatar',
  magicLinkRequest: '/auth/magic-link/request',
  magicLinkVerify: '/auth/magic-link/verify',
  mfaSetup: '/auth/mfa/setup',
  mfaVerifySetup: '/auth/mfa/verify-setup',
  mfaDisable: '/auth/mfa/disable',
  mfaRegenerateBackupCodes: '/auth/mfa/regenerate-backup-codes',
  mfaVerify: '/auth/mfa/verify',
  sessions: '/auth/sessions',
  deleteAccount: '/privacy/delete-account',
  auditLog: '/privacy/audit-log',
} as const

// Login mutation
export const useLogin = () => {
  const { login } = useAuth()

  return useMutation({
    mutationFn: async (credentials: LoginForm) => {
      const response = await apiPost<{
        user: User
        access_token: string
        refresh_token: string
        mfa_required?: boolean
        mfa_challenge_token?: string
      }>(AUTH_ENDPOINTS.login, credentials)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Login failed')
      }

      return response.data
    },
    onSuccess: (data) => {
      // Only login if MFA is not required
      if (!data.mfa_required) {
        login(
          {
            access_token: data.access_token,
          },
          data.user
        )
      }
    },
  })
}

// Register mutation
export const useRegister = () => {
  return useMutation({
    mutationFn: async (userData: RegisterForm) => {
      const response = await apiPost<{
        user: User
        access_token: string
        refresh_token: string
        message: string
      }>(AUTH_ENDPOINTS.register, userData)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Registration failed')
      }

      return response.data
    },
  })
}

// Forgot password mutation
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (data: ForgotPasswordForm) => {
      const response = await apiPost<{ message: string }>(
        AUTH_ENDPOINTS.forgotPassword,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to send reset email')
      }

      return response.data
    },
  })
}

// Reset password mutation
export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: ResetPasswordForm) => {
      const response = await apiPost<{ message: string }>(
        AUTH_ENDPOINTS.resetPassword,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to reset password')
      }

      return response.data
    },
  })
}

// Logout mutation
export const useLogout = () => {
  const { logout } = useAuth()

  return useMutation({
    mutationFn: async () => {
      // Call logout endpoint (optional, depending on backend)
      await apiPost(AUTH_ENDPOINTS.logout, {})
      return true
    },
    onSettled: () => {
      // Always logout on client side, regardless of API call result
      logout()
    },
  })
}

// Get user profile query
export const useUserProfile = () => {
  const { user, isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const response = await apiGet<User>(AUTH_ENDPOINTS.profile)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch profile')
      }

      return response.data
    },
    enabled: isAuthenticated && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Update user profile mutation
export const useUpdateProfile = () => {
  const { updateUser } = useAuth()

  return useMutation({
    mutationFn: async (userData: { name?: string; email?: string }) => {
      const response = await apiPost<User>(AUTH_ENDPOINTS.profile, userData)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update profile')
      }

      return response.data
    },
    onSuccess: (data) => {
      updateUser(data)
    },
  })
}

// Email verification mutation (if needed)
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await apiPost<{ message: string }>('/auth/verify-email', {
        token,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Email verification failed')
      }

      return response.data
    },
  })
}

// Resend verification email mutation (public, no auth required)
export const useResendVerification = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await apiPost<{ message: string }>('/auth/resend-verification-public', {
        email,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to resend verification email')
      }

      return response.data
    },
  })
}

// Magic link request mutation
export const useMagicLinkRequest = () => {
  return useMutation({
    mutationFn: async (data: MagicLinkRequestForm) => {
      const response = await apiPost<{ message: string; email: string }>(
        AUTH_ENDPOINTS.magicLinkRequest,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to send magic link')
      }

      return response.data
    },
  })
}

// Magic link verify mutation
export const useMagicLinkVerify = () => {
  const { login } = useAuth()

  return useMutation({
    mutationFn: async (data: MagicLinkVerifyForm) => {
      const response = await apiPost<{
        user: User
        access_token: string
        refresh_token: string
      }>(AUTH_ENDPOINTS.magicLinkVerify, data)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Magic link verification failed')
      }

      return response.data
    },
    onSuccess: (data) => {
      login(
        {
          access_token: data.access_token,
        },
        data.user
      )
    },
  })
}

// Change password mutation
export const useChangePassword = () => {
  const { logout } = useAuth()

  return useMutation({
    mutationFn: async (data: ChangePasswordForm) => {
      const response = await apiPatch<{ success: boolean; message: string }>(
        AUTH_ENDPOINTS.changePassword,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to change password')
      }

      return response.data
    },
    onSuccess: () => {
      // Logout after password change for security
      // User needs to log in again with new password
      logout()
    },
  })
}

// Request email change mutation
export const useRequestEmailChange = () => {
  const { updateUser } = useAuth()

  return useMutation({
    mutationFn: async (data: EmailChangeRequestForm) => {
      const response = await apiPost<{
        success: boolean
        message: string
        pending_email: string
      }>(AUTH_ENDPOINTS.requestEmailChange, data)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to request email change')
      }

      return response.data
    },
    onSuccess: (data) => {
      // Update user context with pending_email
      updateUser({ pending_email: data.pending_email } as User)
    },
  })
}

// MFA Setup mutation
export const useMFASetup = () => {
  return useMutation({
    mutationFn: async (data: { password: string }) => {
      const response = await apiPost<MFASetupResponse>(AUTH_ENDPOINTS.mfaSetup, data)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to setup MFA')
      }

      return response.data
    },
  })
}

// MFA Verify Setup mutation
export const useMFAVerifySetup = () => {
  const { updateUser } = useAuth()

  return useMutation({
    mutationFn: async (data: MFAVerifySetupForm) => {
      const response = await apiPost<{
        success: boolean
        message: string
        backup_codes: string[]
      }>(AUTH_ENDPOINTS.mfaVerifySetup, data)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to verify MFA setup')
      }

      return response.data
    },
    onSuccess: () => {
      // Update user context to reflect MFA is now enabled
      updateUser({ mfa_enabled: true, mfa_enabled_at: new Date().toISOString() } as User)
    },
  })
}

// MFA Disable mutation
export const useMFADisable = () => {
  const { updateUser } = useAuth()

  return useMutation({
    mutationFn: async (data: MFADisableForm) => {
      const response = await apiPost<{ success: boolean; message: string }>(
        AUTH_ENDPOINTS.mfaDisable,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to disable MFA')
      }

      return response.data
    },
    onSuccess: () => {
      // Update user context to reflect MFA is now disabled
      updateUser({ mfa_enabled: false, mfa_enabled_at: undefined } as User)
    },
  })
}

// MFA Regenerate Backup Codes mutation
export const useMFARegenerateBackupCodes = () => {
  return useMutation({
    mutationFn: async (data: MFARegenerateBackupCodesForm) => {
      const response = await apiPost<MFARegenerateBackupCodesResponse>(
        AUTH_ENDPOINTS.mfaRegenerateBackupCodes,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to regenerate backup codes')
      }

      return response.data
    },
  })
}

// MFA Verify mutation (for login flow)
export const useMFAVerify = () => {
  const { login } = useAuth()

  return useMutation({
    mutationFn: async (data: MFAVerifyForm & { email: string }) => {
      const response = await apiPost<{
        user: User
        access_token: string
        refresh_token: string
      }>(AUTH_ENDPOINTS.mfaVerify, {
        email: data.email,
        totp_code: data.code,
        mfa_challenge_token: data.mfa_challenge_token,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error || 'MFA verification failed')
      }

      return response.data
    },
    onSuccess: (data) => {
      login(
        {
          access_token: data.access_token,
        },
        data.user
      )
    },
  })
}

// Session Management hooks

// Get all sessions for the current user
export const useSessions = () => {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const response = await apiGet<SessionListResponse>(AUTH_ENDPOINTS.sessions)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch sessions')
      }

      return response.data
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Revoke a specific session
export const useRevokeSession = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiDelete<SessionRevokeResponse>(
        `${AUTH_ENDPOINTS.sessions}/${sessionId}`
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to revoke session')
      }

      return response.data
    },
    onSuccess: () => {
      // Invalidate sessions query to refetch
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

// Revoke all sessions except current
export const useRevokeAllSessions = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiDelete<SessionRevokeAllResponse>(
        AUTH_ENDPOINTS.sessions
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to revoke sessions')
      }

      return response.data
    },
    onSuccess: () => {
      // Invalidate sessions query to refetch
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

// Delete account mutation
export const useDeleteAccount = () => {
  const { logout } = useAuth()

  return useMutation({
    mutationFn: async (data: DeleteAccountForm) => {
      const response = await apiPost<DeleteAccountResponse>(
        AUTH_ENDPOINTS.deleteAccount,
        data
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to delete account')
      }

      return response.data
    },
    onSuccess: () => {
      // Log out after account deletion is scheduled
      logout()
    },
  })
}

// Security Activity / Audit Log hooks

// Get security activity (audit log) for the current user
export const useSecurityActivity = (page: number = 1, pageSize: number = 10) => {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['security-activity', page, pageSize],
    queryFn: async () => {
      const response = await apiGet<SecurityActivityResponse>(
        `${AUTH_ENDPOINTS.auditLog}?page=${page}&page_size=${pageSize}`
      )

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch security activity')
      }

      return response.data
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  })
}

// Avatar Management hooks

// Upload avatar mutation
export const useUploadAvatar = () => {
  const { updateUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      // Use fetch directly for multipart/form-data
      const token = authTokens.get().access_token
      const apiUrl = (import.meta as any).env?.VITE_API_URL || ''

      const response = await fetch(`${apiUrl}/api/v1${AUTH_ENDPOINTS.avatar}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to upload avatar')
      }

      const data: AvatarUploadResponse = await response.json()
      return data
    },
    onSuccess: (data) => {
      if (data.avatar_url) {
        updateUser({ avatar_url: data.avatar_url } as User)
      }
      queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
    },
  })
}

// Delete avatar mutation
export const useDeleteAvatar = () => {
  const { updateUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiDelete<AvatarDeleteResponse>(AUTH_ENDPOINTS.avatar)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to delete avatar')
      }

      return response.data
    },
    onSuccess: () => {
      updateUser({ avatar_url: undefined } as User)
      queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
    },
  })
}
